import React, { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import AffiliationService from "../../services/AffiliationService";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import AffiliationCard from "../../components/Affiliation/AffiliationCard/AffiliationCard";
import AffiliationCreatePromocodeModal from "../../components/Affiliation/AffiliationCreatePromocodeModal";
import AffiliationEditPromocodeModal from "../../components/Affiliation/AffiliationEditPromocodeModal";
import AffiliationDeletePromocodeModal from "../../components/Affiliation/AffiliationDeletePromocodeModal";
import AffiliationPromocodeStatusModal from "../../components/Affiliation/AffiliationPromocodeStatusModal";
import MainButton from "../../components/Elements/MainButton/MainButton";
import DataTable from "../../components/Elements/DataTable/DataTable";
import MainInput from "../../components/Elements/MainInput/MainInput";
import UserTransactionsTable from "../../components/Affiliation/UserTransactionsTable/UserTransactionsTable";

import "./Affiliation.css";

const fetchAffiliatePromocodes = (params) => AffiliationService.getAffiliatePromocodeBatch(params);

const Affiliation = () => {
  const { t } = useTranslation();
  const { walletCurrency = "EUR", userId } = useContext(AuthContext);
  const [affiliateData, setAffiliateData] = useState(null);
  const [filters, setFilters] = useState({
    date_start_inclusive: "",
    date_end_exclusive: "",
  });
  const [created, setCreated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [isPromocodeModalOpen, setIsPromocodeModalOpen] = useState(false);
  const [promocodeListVersion, setPromocodeListVersion] = useState(0);
  const [selectedPromocode, setSelectedPromocode] = useState(null);
  const [promocodeAction, setPromocodeAction] = useState(null);

  const columns = [
    { key: "email_private", label: t("affiliation.email", "Email") },
    { key: "total_amount", label: t("affiliation.totalAmount", "Total Amount") },
    { key: "provision", label: t("affiliation.provision", "Commission") },
    { key: "currency", label: t("affiliation.currency", "Currency") },
  ];

  const userStatsRenderers = {
    total_amount: (item) => `${Number(item.total_amount || 0).toFixed(2)} `,
    provision: (item) => `${Number(item.provision || 0).toFixed(2)} `,
  };

  const promocodeColumns = [
    { key: "code", label: t("affiliation.promocodeCode", "Code"), width: "170px" },
    { key: "type", label: t("affiliation.promocodeType", "Type"), width: "150px" },
    { key: "amount", label: t("affiliation.promocodeAmount", "Amount"), width: "260px" },
    { key: "activations_left", label: t("affiliation.activationsLeft", "Uses left"), width: "260px" },
    {
      key: "user_activation_count",
      label: t("affiliation.usesPerUser", "Uses per user"),
      width: "140px",
    },
    { key: "date_end", label: t("affiliation.promocodeExpires", "Expires"), width: "320px" },
    { key: "available", label: t("affiliation.promocodeAvailable", "Available"), width: "110px" },
  ];

  const promocodeSortOptions = [
    { value: "code", label: t("affiliation.promocodeCode", "Code") },
    { value: "type", label: t("affiliation.promocodeType", "Type") },
    { value: "amount", label: t("affiliation.promocodeAmount", "Amount") },
    { value: "activations_left", label: t("affiliation.activationsLeft", "Uses left") },
    { value: "user_activation_count", label: t("affiliation.usesPerUser", "Uses per user") },
    { value: "date_end", label: t("affiliation.promocodeExpires", "Expires") },
  ];

  const promocodeFilters = [
    { key: "code", type: "text" },
    {
      key: "type",
      type: "select",
      options: [
        { value: "topup_amount", label: t("affiliation.promocodeTopupAmount", "Top-up amount") },
        { value: "sell_discount", label: t("affiliation.promocodeSellDiscount", "Sell discount") },
      ],
    },
    { key: "amount", type: "minmax", inputType: "number", minKey: "amount_min", maxKey: "amount_max" },
    {
      key: "activations_left",
      type: "minmax",
      inputType: "number",
      minKey: "activations_left_min",
      maxKey: "activations_left_max",
    },
    { key: "date_end", type: "date-range", minKey: "date_end_min", maxKey: "date_end_max" },
  ];

  const promocodeRenderers = {
    type: (item) => {
      if (item.type === "topup_amount") return t("affiliation.promocodeTopupAmount", "Top-Up Amount");
      if (item.type === "sell_discount") return t("affiliation.promocodeSellDiscount", "Sell Discount");
      return item.type || "-";
    },
    amount: (item) => {
      if (item.amount == null) return "-";
      if (item.type === "sell_discount") return `${item.amount}%`;
      return `${item.amount} ${item.currency || ""}`.trim();
    },
    date_end: (item) => (item.date_end ? new Date(item.date_end).toLocaleDateString() : "-"),
    available: (item) => (item.available ? t("common.yes", "Yes") : t("common.no", "No")),
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const affiliateRes = await AffiliationService.getAffiliation();
        setAffiliateData(affiliateRes);
        setCreated(!!affiliateRes.wallet_id);
      } catch (err) {
        if (err.response?.status === 404) {
          setCreated(false);
          setAffiliateData(null);
        } else {
          setError(t("errors.failedToLoadAffiliateData", "Failed to load affiliate data"));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const handleCreateAffiliation = async () => {
    if (creating) return;

    const confirmed = window.confirm(
      t("affiliation.confirmCreate", "Are you sure you want to create an Affiliation account?"),
    );

    if (!confirmed) return;

    try {
      setCreating(true);
      await AffiliationService.createAffiliation();

      const affiliateRes = await AffiliationService.getAffiliation();
      setAffiliateData(affiliateRes);
      setCreated(true);
    } catch (err) {
      setError(t("errors.failedToCreateAffiliation", "Failed to create affiliation"));
    } finally {
      setCreating(false);
    }
  };

  const fetchAffiliateStats = useCallback(async () => {
    const data = await AffiliationService.getAffiliationStats(filters.date_start_inclusive, filters.date_end_exclusive);

    return {
      items: data.filtered?.users || [],
      total: data.total.total_users_registered,
      filtered: data.filtered?.users_registered,
    };
  }, [filters.date_start_inclusive, filters.date_end_exclusive]);

  const handleEditPromocode = (promocode) => {
    setSelectedPromocode(promocode);
    setPromocodeAction("edit");
  };

  const handleDeletePromocode = (promocode) => {
    setSelectedPromocode(promocode);
    setPromocodeAction("delete");
  };

  const handlePromocodeStatusChange = (action, promocode) => {
    setSelectedPromocode(promocode);
    setPromocodeAction(action);
  };

  const closePromocodeAction = () => {
    setPromocodeAction(null);
    setSelectedPromocode(null);
  };

  const refreshPromocodeList = () => {
    setPromocodeListVersion((version) => version + 1);
  };

  const promocodeRowActions = (promocode) => [
    { label: t("common.edit", "Edit"), onClick: () => handleEditPromocode(promocode) },
    { label: t("common.delete", "Delete"), onClick: () => handleDeletePromocode(promocode) },
    promocode.available
      ? {
          label: t("affiliation.disable", "Disable"),
          onClick: () => handlePromocodeStatusChange("disable", promocode),
        }
      : {
          label: t("affiliation.activate", "Activate"),
          onClick: () => handlePromocodeStatusChange("activate", promocode),
        },
  ];

  const createButton = !created && !loading && (
    <MainButton className="btn btn-primary" onClick={handleCreateAffiliation} disabled={creating}>
      {creating ? t("affiliation.creating", "Creating...") : t("affiliation.createAffiliation", "Create Affiliation")}
    </MainButton>
  );

  if (loading) {
    return (
      <div className="affiliation-page">
        <div>{t("common.loading", "Loading...")}</div>
      </div>
    );
  }

  return (
    <div className="affiliation-page">
      <PageHeader
        title={t("affiliation.title", "Affiliation")}
        description={t("affiliation.subtitle", "Manage your referral link and earnings")}
        actions={createButton}
      />

      {error && <div className="error-message">{error}</div>}

      <div className="affiliation-container">
        <AffiliationCard key={created ? "created" : "not-created"} />
      </div>

      {created && (
        <>
          <section className="affiliation-promocodes-section">
            <div className="affiliation-promocodes-heading">
              <div className="affiliation-promocodes-title-row">
                <div className="affiliation-promocodes-copy">
                  <h2>{t("affiliation.promocodesTitle", "Affiliation promocodes")}</h2>
                  <p>
                    {t("affiliation.promocodesDescription", "Promocodes associated with your affiliation account.")}
                  </p>
                </div>
                <MainButton onClick={() => setIsPromocodeModalOpen(true)}>
                  {t("affiliation.createPromocode", "Promocode +")}
                </MainButton>
              </div>
            </div>
            <DataTable
              key={promocodeListVersion}
              fetchDataFunction={fetchAffiliatePromocodes}
              columns={promocodeColumns}
              sortOptions={promocodeSortOptions}
              filterDefinitions={promocodeFilters}
              customRenderers={promocodeRenderers}
              rowActions={promocodeRowActions}
              rowActionsMode="select"
              defaultSortBy="code"
              itemKey="code"
              showSort={true}
              showFilters={true}
              showPageSize={false}
              defaultPageSize={5}
              showCheckboxes={false}
              tableId="affiliation-promocodes-table"
              emptyMessage={t("affiliation.noPromocodes", "No affiliation promocodes found")}
            />
            <AffiliationCreatePromocodeModal
              isOpen={isPromocodeModalOpen}
              onClose={() => setIsPromocodeModalOpen(false)}
              onCreated={() => {
                setPromocodeListVersion((version) => version + 1);
              }}
            />
            <AffiliationEditPromocodeModal
              isOpen={promocodeAction === "edit"}
              onClose={closePromocodeAction}
              promocode={selectedPromocode}
              onUpdated={refreshPromocodeList}
            />
            <AffiliationDeletePromocodeModal
              isOpen={promocodeAction === "delete"}
              onClose={closePromocodeAction}
              promocode={selectedPromocode}
              onDeleted={refreshPromocodeList}
            />
            <AffiliationPromocodeStatusModal
              isOpen={promocodeAction === "activate" || promocodeAction === "disable"}
              action={promocodeAction}
              onClose={closePromocodeAction}
              promocode={selectedPromocode}
              onCompleted={refreshPromocodeList}
            />
          </section>

          <div className="affiliation-date-filters">
            <div className="affiliation-promocodes-heading affiliation-users-heading">
              <h2>{t("affiliation.usersTitle", "Affiliated users")}</h2>
              <p>{t("affiliation.usersDescription", "Users registered through your affiliation link.")}</p>
            </div>
            <div className="affiliation-date-filters__row">
              <div className="affiliation-date-filters__field">
                <label className="dt-label">{t("affiliation.fromDate", "From date")}</label>
                <MainInput
                  type="date"
                  value={filters.date_start_inclusive || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      date_start_inclusive: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="affiliation-date-filters__field">
                <label className="dt-label">{t("affiliation.toDate", "To date")}</label>
                <MainInput
                  type="date"
                  value={filters.date_end_exclusive || ""}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      date_end_exclusive: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="affiliation-stats-table">
            <DataTable
              fetchDataFunction={fetchAffiliateStats}
              columns={columns}
              customRenderers={userStatsRenderers}
              renderExpandedRow={(user) => <UserTransactionsTable transactions={user.transactions} />}
              showSort={false}
              defaultSortBy="email_private"
              itemKey="email_private"
              showFilters={false}
              showPageSize={false}
              showCheckboxes={false}
              defaultPageSize={5}
              tableId="affiliation-stats-table"
              emptyMessage={t("affiliation.noAffiliatedUsers", "No affiliated users found")}
              expandColumnLabel={t("affiliation.transactions", "Transactions")}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Affiliation;
